import { useState, useEffect } from 'react';
import { apiClient } from '../Utils/apiClient';
import { toast } from 'sonner';

interface ModelThinkingConfig {
  modelName: string;
  thinkingModeEnabled: boolean;
  thinkingLevelOptions?: string[] | null;
  thinkingBudgetType: 'N/A' | 'numeric';
  budgetRange?: { min: number; max: number } | null;
  status: 'active' | 'discontinued';
  description: string;
  shutdownDate?: string;
}

interface ValidationResult {
  success: boolean;
  valid: boolean;
  message: string;
  validOptions?: string[];
  budgetRange?: { min: number; max: number };
}

export function useModelThinkingConfig(modelId: string, token?: string | null) {
  const [config, setConfig] = useState<ModelThinkingConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    if (!modelId) return;

    setLoading(true);
    setError(null);

    try {
      const effectiveToken = token ?? localStorage.getItem('token') ?? undefined;
      const rawResponse = await apiClient(`models/thinking/config/${modelId}`, {
        method: 'GET',
        token: effectiveToken,
      });

      const data = await rawResponse.json();

      if (data?.success) {
        setConfig(data.data);
        setError(null);
      } else {
        throw new Error(data?.error || 'Failed to fetch model config');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch model configuration';
      setError(errorMessage);
      const status = err?.status;
      if (status !== 404) {
        toast.error('Failed to load model configuration', {
          description: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [modelId, token]);

  return { config, loading, error, refetch: fetchConfig };
}

export function useThinkingValidation() {
  const [validating, setValidating] = useState(false);

  const validateThinkingLevel = async (
    modelId: string,
    thinkingLevel: string
  ): Promise<ValidationResult> => {
    setValidating(true);
    try {
      const rawResponse = await apiClient('models/validate/thinking/level', {
        method: 'POST',
        body: { modelId, thinkingLevel }
      });
      const response = await rawResponse.json();

      return {
        success: response.success,
        valid: response.valid,
        message: response.message,
        validOptions: response.validOptions
      };
    } catch (error) {
      return {
        success: false,
        valid: false,
        message: 'Validation failed'
      };
    } finally {
      setValidating(false);
    }
  };

  const validateThinkingBudget = async (
    modelId: string,
    budget: number
  ): Promise<ValidationResult> => {
    setValidating(true);
    try {
      const rawResponse = await apiClient('models/validate/thinking/budget', {
        method: 'POST',
        body: { modelId, budget }
      });
      const response = await rawResponse.json();

      return {
        success: response.success,
        valid: response.valid,
        message: response.message,
        budgetRange: response.budgetRange
      };
    } catch (error) {
      return {
        success: false,
        valid: false,
        message: 'Validation failed'
      };
    } finally {
      setValidating(false);
    }
  };

  return {
    validateThinkingLevel,
    validateThinkingBudget,
    validating
  };
}